import client from 'GqlClient';
import { pick } from 'lodash';
import { createTaggedLogger } from 'logging/createTaggedLogger';
import { useEffect, useState } from 'react';
import {
  CityPageContentDocument,
  CityPageContentQuery,
  CityPageDishFragment,
  CityPageFilterFragment,
  CityPageShefFragment,
  RegionPageContentDocument,
} from 'utils/gqlReactTypings.generated.d';

interface UseCityPageContentParams {
  zipCode?: string | null;
  regionId?: number;
  filter?: CityPageFilterFragment;
  loggableName: string; // Just for quickly finding why errors occur. Not expected to happen.
}

interface CityPageContent {
  dishes?: CityPageDishFragment[];
  shefs?: CityPageShefFragment[];
  numDishes?: number;
}

interface UseCityPageContent extends CityPageContent {
  isLoading: boolean;
}

const queryPageContent = (params: UseCityPageContentParams) => {
  if (params.zipCode) {
    return client.query<CityPageContentQuery>({
      query: CityPageContentDocument,
      variables: {
        zipCode: params.zipCode,
        filter: pick(params.filter, ['dietaryFilter', 'cuisineCategoryId']),
      },
      fetchPolicy: 'cache-first',
    });
  }

  if (params.regionId) {
    return client.query<CityPageContentQuery>({
      query: RegionPageContentDocument,
      variables: {
        regionId: params.regionId,
        filter: pick(params.filter, ['dietaryFilter', 'cuisineCategoryId']),
      },
      fetchPolicy: 'cache-first',
    });
  }

  throw new Error(`ZipCode and RegionId are both null for ${params.loggableName}`);
};

const contentField = (params: UseCityPageContentParams): 'cityPageContent2' | 'cityPageRegionContent' => {
  if (params.zipCode) {
    return 'cityPageContent2';
  }

  if (params.regionId) {
    return 'cityPageRegionContent';
  }

  throw new Error(`ZipCode and RegionId are both null for ${params.loggableName}`);
};

const logger = createTaggedLogger('use-city-page-content');

export const useCityPageContent = (params: UseCityPageContentParams): UseCityPageContent => {
  const [cityPageContent, setCityPageContent] = useState<CityPageContent>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    queryPageContent(params)
      .then(({ data, errors }) => {
        if (errors) {
          logger.error('Query for city page content has apollo errors', { errors });
          return;
        }

        if (!data) {
          logger.error('Query for city page content did not return data');
          return;
        }

        const field = contentField(params);
        setCityPageContent({
          shefs: data[field].shefs,
          dishes: data[field].dishes,
          numDishes: data[field].numDishes,
        });
      })
      .catch((error) => logger.error('Query for city page content failed', { error }))
      .finally(() => setIsLoading(false));
  }, [params]);

  return { ...cityPageContent, isLoading };
};
